import { CircleHelp } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCodeEditor,
  IgLink,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { CustomEventDto } from "@intelligems/ig-types";
import { generateCustomEventIdentifier } from "@intelligems/ig-utils";
import { Box, ButtonGroup, Grid, IconButton, useTheme } from "@mui/material";
import { useGetUnregisteredTinybirdEvents } from "@src/api/custom-events/get-unregistered-tinybird-events";
import { CustomEventPageTargetingWrapper } from "@src/features/customEvents/components/CustomEventPageTargetingWrapper";
import { JsEventTable } from "@src/features/customEvents/components/JsEventTable";
import {
  initCustomEvent,
  selectedCurrentCustomEvent,
  setCustomEventUrls,
  setCustomJsEventCode,
  setCustomJsEventIdentifier,
  switchCustomJsEventType,
} from "@src/store/slices/customEvents";
import { customJavascriptEventLink } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface JsEventProps {
  isArchived: boolean;
}

const defaultCode = (id: string) =>
  `// Add an event listener for your custom event
  myElement.addEventListener("mouseover", (event) => {
    // Create the igEvents array if it doesn't exist
    window.igEvents = window.igEvents || [];
    // Add your event to the igEvents array
    window.igEvents.push({
      "event": "${id}", // Do not remove - required for tracking
    })
  });`.replace(/^ {2}/gm, "");

export const JsEvent = ({ isArchived }: JsEventProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const unregisteredEvents = useGetUnregisteredTinybirdEvents();
  const currentEvent = useSelector(selectedCurrentCustomEvent);

  const [existingEvent, _] = useImmer<CustomEventDto | undefined>(() =>
    // The existing event actively being edited.
    currentEvent?.identifier ? (currentEvent as CustomEventDto) : undefined
  );

  const [isEventNotFound, setIsEventNotFound] = useImmer<boolean>(false);

  return (
    <Grid container justifyContent={"center"}>
      {!isArchived && (
        <ButtonGroup
          fullWidth={true}
          sx={{ marginTop: "20px", height: "36px", width: "100%" }}
        >
          <IgButton
            disableElevation={true}
            sx={{
              color: theme.palette.text.primary,
              fontSize: "12px",
              borderRadius: "30px",
              border: `1px solid ${theme.igBorders.core.default}`,
              textTransform: "none",
              backgroundColor:
                currentEvent?.type === "javascriptEvent" ? "#EBEBEB" : "white",

              "&:hover": {
                border: `1px solid ${theme.igBorders.core.hover}`,
              },
              minHeight: "36px",
            }}
            onClick={() => {
              dispatch(switchCustomJsEventType({ tab: 0 }));
              setIsEventNotFound(false);
              if (existingEvent) {
                dispatch(initCustomEvent({ customEvent: existingEvent }));
              } else {
                const identifier = generateCustomEventIdentifier({
                  name: "javascriptEvent",
                });
                dispatch(setCustomJsEventIdentifier({ identifier }));
                dispatch(
                  setCustomJsEventCode({
                    code: defaultCode(identifier),
                  })
                );
              }
            }}
          >
            Define an Event
          </IgButton>
          <IgButton
            disableElevation={true}
            sx={{
              borderRadius: "30px",
              minHeight: "36px",
              color: theme.palette.text.primary,
              fontSize: "12px",
              border: `1px solid ${theme.igBorders.core.default}`,
              textTransform: "none",
              backgroundColor:
                currentEvent?.type === "javascriptImportedEvent"
                  ? "#EBEBEB"
                  : "white",
              "&:hover": {
                border: `1px solid ${theme.igBorders.core.hover}`,
              },
            }}
            onClick={() => {
              dispatch(switchCustomJsEventType({ tab: 1 }));
              dispatch(setCustomJsEventCode({ code: "" }));
              dispatch(setCustomEventUrls({ urls: [] }));
            }}
          >
            Choose Existing Event
          </IgButton>
        </ButtonGroup>
      )}
      <Grid
        container
        direction={"column"}
        marginTop={"30px"}
        alignItems={"flex-start"}
        wrap={"nowrap"}
      >
        {currentEvent?.type === "javascriptEvent" ? (
          <Grid item>
            <IgTypography fontWeight={600}>Event Code</IgTypography>
            <br />
            <IgTypography color={theme.palette.text.secondary}>
              Define your event in javascript. The code will be injected into
              the site theme.{" "}
              <IgLink
                onClick={() => {
                  openNewTab(
                    customJavascriptEventLink,
                    "Custom Javascript Events"
                  );
                }}
              >
                Learn more.
              </IgLink>
            </IgTypography>{" "}
          </Grid>
        ) : (
          <Grid item>
            <IgTypography fontWeight={600}>Choose an Event</IgTypography>
            <br />
            <IgTypography color={theme.palette.text.secondary}>
              Pick a javascript event already defined in your site’s theme code.
            </IgTypography>{" "}
          </Grid>
        )}
      </Grid>
      {currentEvent?.type === "javascriptEvent" && (
        <Grid container width={"100%"}>
          <Box
            sx={{
              marginTop: "15px",
              width: "100%",
              border: "1px solid #ccc",
            }}
          >
            <IgCodeEditor
              language="js"
              height="250px"
              value={currentEvent.settings?.code || ""}
              onChange={(value) =>
                dispatch(setCustomJsEventCode({ code: value || "" }))
              }
            />
          </Box>
          <Grid item marginTop={"15px"}>
            <IgTypography fontWeight={600}>
              Event Identifier (generated)
            </IgTypography>
            <IgTooltip
              title={
                "The event identifier is generated for use in analytics tracking. It cannot be changed and is used only internally."
              }
              placement={"right"}
              sx={{ width: "250px" }}
            >
              <IconButton size={"small"}>
                <CircleHelp size={16} />
              </IconButton>
            </IgTooltip>
            <br />
            <IgTypography color={theme.palette.text.secondary}>
              {currentEvent?.identifier}
            </IgTypography>
          </Grid>
          <Grid item marginTop={"15px"} width={"100%"}>
            <CustomEventPageTargetingWrapper
              buttonText={"Add a Page"}
              showHeader
              showInitial={false}
              isArchived={isArchived}
            />
          </Grid>
        </Grid>
      )}
      {currentEvent?.type === "javascriptImportedEvent" && (
        <JsEventTable
          events={unregisteredEvents?.data?.custom_events || []}
          onEventNotListed={() => setIsEventNotFound(true)}
        />
      )}
      {isEventNotFound && (
        <Grid container marginTop={"30px"}>
          <IgTypography fontWeight={600}>
            Events that haven’t triggered recently will not be listed above, but
            you can still type in the identifier of the event as defined in your
            site’s javascript.
          </IgTypography>
          <IgTextField
            fullWidth
            placeholder={
              "What is your event's identifier? (ex: abandoned-checkout)"
            }
            sx={{ marginTop: "15px" }}
            value={currentEvent?.identifier || ""}
            onChange={(e) =>
              dispatch(
                setCustomJsEventIdentifier({ identifier: e.target.value })
              )
            }
          />
        </Grid>
      )}
    </Grid>
  );
};
